<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl" v-if="!isLoading">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          Edit Announcement
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.title"
                outlined
                label="Title *"
                prepend-inner-icon="mdi-bullhorn"
                :error-messages="errors.title"
                counter="100"
                maxlength="100"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.message"
                outlined
                label="Subtitle"
                prepend-inner-icon="mdi-subtitles"
                :error-messages="errors.message"
                counter="225"
                maxlength="225"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.audience_type"
                :items="audienceTypes"
                item-text="text"
                item-value="value"
                outlined
                label="Audience Type"
                prepend-inner-icon="mdi-account-group"
                :error-messages="errors.audience_type"
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-menu
                v-model="publishDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="publishedAtFormatted"
                    label="Publication Date & Time"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    v-bind="attrs"
                    v-on="on"
                    :error-messages="errors.published_at"
                    clearable
                    @click:clear="clearPublishDate"
                  ></v-text-field>
                </template>
                <v-card>
                  <v-card-title class="primary white--text">
                    Select Publication Date & Time
                  </v-card-title>
                  <v-card-text>
                    <v-date-picker
                      v-model="publishDate"
                      no-title
                      scrollable
                    ></v-date-picker>
                    <v-time-picker
                      v-if="publishDate"
                      v-model="publishTime"
                      format="24hr"
                      scrollable
                    ></v-time-picker>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="publishDateMenu = false">
                      Cancel
                    </v-btn>
                    <v-btn text color="primary" @click="setPublishDateTime">
                      OK
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.description"
                outlined
                label="Description"
                prepend-inner-icon="mdi-text"
                :error-messages="errors.description"
                rows="5"
                counter
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Publication Status Display -->
          <v-row v-if="form.id">
            <v-col cols="12">
              <v-alert
                :type="getStatusType()"
                :icon="getStatusIcon()"
                outlined
                prominent
              >
                <div class="title">{{ getStatusTitle() }}</div>
                <div>{{ getStatusMessage() }}</div>
              </v-alert>
            </v-col>
          </v-row>

          <v-divider class="mt-5 mb-5"></v-divider>
          <v-btn
            :loading="isSaving"
            :disabled="isSaving"
            rounded
            color="primary"
            dark
            @click="save"
          >
            Update Announcement
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Loading State -->
      <v-card elevation="6" rounded="xxl" v-else>
        <v-card-text class="text-center" style="height: 400px;">
          <health-loader message="Loading announcement data..." />
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import { splitIstDateTime, combineIstDateTime, APP_TIMEZONE } from "@/utils/dateTime";

export default {
  name: "AnnouncementEditPage",
  data() {
    return {
      form: {
        id: null,
        title: "",
        message: "",
        description: "",
        audience_type: null,
        published_at: null,
        is_active: true,
      },
      errors: {},
      isLoading: true,
      isSaving: false,
      publishDateMenu: false,
      publishDate: null,
      publishTime: null,
      audienceTypes: [
        { text: "All Users", value: "all_users" },
        { text: "Doctors", value: "doctors" },
        { text: "Patients", value: "patients" },
      ],
    };
  },
  computed: {
    publishedAtFormatted() {
      if (!this.form.published_at) return null;
      const date = new Date(this.form.published_at);
      return date.toLocaleString("en-IN", { timeZone: APP_TIMEZONE });
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      try {
        this.isLoading = true;
        const encryptedId = this.$route.params.id;
        const response = await axios.get(`/announcements/${encryptedId}`);

        if (response.data.success) {
          this.form = { ...response.data.data };
          
          // Parse published_at for date/time pickers
          if (this.form.published_at) {
            const { date, time } = splitIstDateTime(this.form.published_at);
            this.publishDate = date;
            this.publishTime = time;
          }
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to load announcement",
          });
          this.$router.push("/announcements");
        }
      } catch (error) {
        console.error("Error loading announcement:", error);
        
        if (error.response && error.response.status === 404) {
          this.$store.dispatch("errors/setError", {
            message: "Announcement not found",
          });
        } else if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error loading announcement data",
          });
        }
        this.$router.push("/announcements");
      } finally {
        this.isLoading = false;
      }
    },

    setPublishDateTime() {
      if (this.publishDate) {
        this.form.published_at = combineIstDateTime(this.publishDate, this.publishTime);
      }
      this.publishDateMenu = false;
    },

    clearPublishDate() {
      this.form.published_at = null;
      this.publishDate = null;
      this.publishTime = null;
    },

    getStatus() {
      if (!this.form.is_active) return 'Inactive';
      if (!this.form.published_at) return 'Draft';
      
      const publishedAt = new Date(this.form.published_at);
      const now = new Date();
      
      if (publishedAt > now) return 'Scheduled';
      return 'Published';
    },

    getStatusType() {
      const status = this.getStatus();
      switch (status) {
        case 'Published': return 'success';
        case 'Draft': return 'warning';
        case 'Scheduled': return 'info';
        case 'Inactive': return 'error';
        default: return 'info';
      }
    },

    getStatusIcon() {
      const status = this.getStatus();
      switch (status) {
        case 'Published': return 'mdi-publish';
        case 'Draft': return 'mdi-file-document-outline';
        case 'Scheduled': return 'mdi-clock-outline';
        case 'Inactive': return 'mdi-close-circle';
        default: return 'mdi-help-circle';
      }
    },

    getStatusTitle() {
      return `Status: ${this.getStatus()}`;
    },

    getStatusMessage() {
      const status = this.getStatus();
      switch (status) {
        case 'Published': 
          return `This announcement was published on ${this.formatDate(this.form.published_at)}`;
        case 'Draft': 
          return 'This announcement is saved as a draft and is not yet published';
        case 'Scheduled': 
          return `This announcement is scheduled to be published on ${this.formatDate(this.form.published_at)}`;
        case 'Inactive': 
          return 'This announcement is inactive and will not be visible to users';
        default: 
          return 'Unknown status';
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString("en-IN", { timeZone: APP_TIMEZONE });
    },

    async save() {
      try {
        this.isSaving = true;
        this.errors = {};

        const encryptedId = this.$route.params.id;
        const response = await axios.patch(`/announcements/${encryptedId}`, this.form);

        if (response.data.success) {
          this.$store.dispatch("errors/setSuccess", {
            message: "Announcement updated successfully",
          });
          this.$router.push("/announcements");
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "Failed to update announcement",
          });
        }
      } catch (error) {
        console.error("Error updating announcement:", error);
        
        if (error.response && error.response.status === 422) {
          this.errors = error.response.data.errors || {};
        } else if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error updating announcement",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>